import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Typography } from '~/components/elements/Typography'
import {
  NineSegmentsCheckPanel,
  type NineSegmentsCheckPanelProps,
} from '~/components/features/NineSegmentsCheckPanel'
import { SelectorListContainer } from '~/components/features/SelectorListContainer'
import { SurveyGroupBrandSelector } from '~/components/features/SurveyGroupBrandSelector'
import { useFormField } from '~/hooks/useFormField'
import { type NineNumber, toNineNumber, toNineSegment } from '~/utils'

import { type InputValues, type SurveyGroupBrand } from '../utils'

import styles from './BrandSegmentInput.css'

export type BrandSegmentInputProps = {
  brands: SurveyGroupBrand[]
  control: Control<InputValues>
  filterGroupIndex: number
  filterIndex: number
}

export const BrandSegmentInput = ({
  brands,
  control,
  filterGroupIndex,
  filterIndex,
}: BrandSegmentInputProps) => {
  const { t } = useTranslation()
  const namePrefix =
    `filterGroups.${filterGroupIndex}.${filterIndex}.brandSegments` as const
  const brandIdField = useFormField(control, `${namePrefix}.surveyGroupBrandId`)
  const segmentNumbersField = useFormField(
    control,
    `${namePrefix}.segmentNumbers`,
  )

  const nineSegmentsCheckPanelValue = React.useMemo(
    () => (segmentNumbersField.value as NineNumber[]).map(toNineSegment),
    [segmentNumbersField.value],
  )
  const handleNineSegmentsChange = React.useCallback<
    EH<NineSegmentsCheckPanelProps, 'onChange'>
  >(
    (nineSegments) => {
      const segmentNumbers = nineSegments.map(toNineNumber)
      segmentNumbersField.onChange(segmentNumbers)
    },
    [segmentNumbersField],
  )

  return (
    <SelectorListContainer>
      <Typography fontWeight={700} variant="heading3">
        {t('brand_name_10')}
      </Typography>

      <SurveyGroupBrandSelector brands={brands} {...brandIdField} />

      <NineSegmentsCheckPanel
        className={styles.segmentNumbersCheckPanel}
        value={nineSegmentsCheckPanelValue}
        onChange={handleNineSegmentsChange}
      />
    </SelectorListContainer>
  )
}
